/* eslint-disable no-param-reassign */
import cloneDeep from 'lodash/cloneDeep';
import { v4 } from 'uuid';
import { Dispatch } from 'redux';
import toast, { superAdminToast } from '../../../../utils/toast';
import { postVirtualClipFromApi } from '../../../AssetDetails/api/assetsVirtualClips';
import { selectSelectedClips, setAllClipsSelected } from '../clipList';
import { updateSelectAll } from '../buttons';
import { getLiveVideoUrl, getAssetFromApi } from '../../../AssetDetails/api/assetsLookupsMetatags';
import { ActionStatus, ActionType, IMetadata } from '../../../../api/vault/assetStatus/interfaces';
import { createAssetStatus } from '../../../../api/vault/assetStatus/api';
import { selectSecurityProfileId } from '../../../../state/securityProfiles';
import { VAULT_VIDEO_TYPE } from '../../../../constants/vault';
import { IAppState } from '../../../../state/reducers';

const queued = [ActionType.Download, ActionType.PublishTwitter, ActionType.Publish]; // add other publishing options here

interface ICreateAssetParams {
  newAsset: any;
  metadata: IMetadata[];
  actionType?: ActionType;
}

export const createAsset = ({
  newAsset,
  metadata,
  actionType = ActionType.Create,
}: ICreateAssetParams) => async (dispatch: Dispatch, getState: () => IAppState) => {
  const state = getState();
  const selectedClips = cloneDeep(selectSelectedClips(state));
  const securityProfileId = selectSecurityProfileId(state);
  const {
    clipperMediaContent: {
      assetDetails: {
        _id,
        sport,
        gameDate,
        homeTeam,
        awayTeam,
        sha1,
        previewImageUrl,
        thumbnailImageUrl,
        exchangeLiveStreamScheduleId,
      },
    },
    user: {
      userOrgRole: {
        loginAs, useAs,
      },
    },
    selectedClipsPreview: {
      preRoll,
      postRoll,
    },
  } = state;

  let vt = VAULT_VIDEO_TYPE.Clip;
  let source = sha1 || null;
  if (!sha1) {
    // !sha1 means it's a live event
    let currentSha1 = null;
    try {
      currentSha1 = (await getAssetFromApi(_id)).sha1;
    } catch {
      // ignore missing
    }
    if (currentSha1) {
      superAdminToast(false, 'Unable to save clips. Live stream finished, please close and re-open clipper.', { type: toast.TYPE.ERROR });
      return;
    }
    source = await getLiveVideoUrl(_id);
    if (source) vt = VAULT_VIDEO_TYPE.LiveClip;
  }
  updateSelectAll(false)(dispatch, getState);
  setAllClipsSelected(false)(dispatch, getState);

  if (preRoll) {
    selectedClips.unshift({
      id: v4(),
      sortIndex: 0,
      name: preRoll.asset._source.title,
      markInTime: 0,
      markOutTime: preRoll.duration,
      sha1: preRoll.asset._source.sha1,
    });
  }
  if (postRoll) {
    selectedClips.push({
      id: v4(),
      sortIndex: selectedClips.length,
      name: postRoll.asset._source.title,
      markInTime: 0,
      markOutTime: postRoll.duration,
      sha1: postRoll.asset._source.sha1,
    });
  }

  const userName = state.user?.userOrgRole?.loginAs?.userInfo?.displayName ?? '';
  const virtualClips = {
    assetId: _id,
    title: newAsset.title,
    description: newAsset.description || null,
    sport: sport || null,
    gameDate: gameDate || null,
    homeTeam: homeTeam || null,
    awayTeam: awayTeam || null,
    type: 'Clip',
    clipList: selectedClips,
    sha1: source,
    videoType: vt,
    actionType,
    previewImageUrl: previewImageUrl || null,
    thumbnailImageUrl: thumbnailImageUrl || null,
    createdBy: userName,
    securityProfiles: [newAsset.securityProfileId || securityProfileId],
    owner: newAsset.securityProfileId || securityProfileId,
    watermarkAssetId: newAsset.watermarkAssetId,
    ...(exchangeLiveStreamScheduleId && { exchangeLiveStreamScheduleId }),
  };
  const role = useAs || loginAs;
  const userHasAccess = role?.userAccessPermission?.manageVideoClip ?? false;
  if (userHasAccess) {
    const res = await postVirtualClipFromApi(virtualClips);
    if (exchangeLiveStreamScheduleId) {
      if (res.success) {
        toast.info(res.message);
      } else {
        toast.error(res.message);
      }
    } else if (!res?._id) {
      superAdminToast(false, 'Unable to save clips. Please try again or cancel.', { type: toast.TYPE.ERROR });
    } else {
      superAdminToast(false, `Generating Clip:\n ${res.title}`, { type: toast.TYPE.INFO });
      console.log(`[QA] Clips saved! New asset id: ${res._id}, title: ${res.title}`);
      if (queued.includes(actionType)) {
        await createAssetStatus({
          assetName: res.title,
          assetId: res._id,
          actionType,
          actionStatus: ActionStatus.Queued,
          actionProgress: null,
          videoType: 'clips',
          metadata,
        });
      }
    }
  } else {
    superAdminToast(false, 'User has insufficient access to complete this action', { type: toast.TYPE.ERROR });
  }
};
